// ─────────────────────────────────────────────────────────────────
// Lägger modellens mask på bilden i full upplösning. Masken räknas fram på
// en nedskalad bild (modellen arbetar i 1024×1024), skalas här upp bilinjärt
// till originalets storlek och används som alfa mot en ogenomskinlig
// bakgrundsfärg — så skärpan i originalet behålls. Ren funktion utan DOM,
// så den kan enhetstestas.
// ─────────────────────────────────────────────────────────────────

export interface Rgba {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

export interface Mask {
  /** Ett värde per pixel, 0 = bakgrund, 255 = plagg. */
  data: Uint8Array | Uint8ClampedArray;
  width: number;
  height: number;
}

export type Rgb = [number, number, number];

export function parseHexColor(hex: string): Rgb {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex.trim());
  if (!m) throw new Error(`Ogiltig färg: ${hex}`);
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
}

/**
 * Sträcker masken så att dess lägsta värde blir 0 och högsta 255 — samma
 * efterbehandling som rembg gör för ISNet, så att svaga kanter inte lämnar
 * en grå slöja av bakgrunden. En helt jämn mask lämnas orörd.
 */
export function stretchMask(mask: Mask): Mask {
  let min = 255, max = 0;
  for (const v of mask.data) { if (v < min) min = v; if (v > max) max = v; }
  if (max <= min || (min === 0 && max === 255)) return mask;
  const range = max - min;
  const data = new Uint8Array(mask.data.length);
  for (let i = 0; i < data.length; i++) data[i] = Math.round(((mask.data[i] - min) * 255) / range);
  return { data, width: mask.width, height: mask.height };
}

/** Skalar ner (aldrig upp) så att längsta sidan blir högst maxSide. */
export function fitWithin(width: number, height: number, maxSide: number): { width: number; height: number } {
  const scale = Math.min(1, maxSide / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

// Förberäknar, för varje utpixel längs en axel, de två närmaste maskindexen
// och vikten mellan dem (pixelcentrum-justerat, som canvas drawImage).
function axisWeights(outSize: number, inSize: number) {
  const i0 = new Int32Array(outSize);
  const i1 = new Int32Array(outSize);
  const w = new Float32Array(outSize);
  const scale = inSize / outSize;
  for (let o = 0; o < outSize; o++) {
    const pos = Math.min(Math.max((o + 0.5) * scale - 0.5, 0), inSize - 1);
    const lo = Math.floor(pos);
    i0[o] = lo;
    i1[o] = Math.min(lo + 1, inSize - 1);
    w[o] = pos - lo;
  }
  return { i0, i1, w };
}

export function applyMask(image: Rgba, mask: Mask, background: Rgb): Uint8ClampedArray<ArrayBuffer> {
  const { width, height, data } = image;
  if (data.length !== width * height * 4) throw new Error('Bilddata har fel storlek');
  if (mask.data.length !== mask.width * mask.height) throw new Error('Masken har fel storlek');

  const out = new Uint8ClampedArray(data.length);
  const xs = axisWeights(width, mask.width);
  const ys = axisWeights(height, mask.height);
  const [br, bg, bb] = background;
  const m = mask.data;
  const mw = mask.width;

  for (let y = 0; y < height; y++) {
    const row0 = ys.i0[y] * mw;
    const row1 = ys.i1[y] * mw;
    const wy = ys.w[y];
    for (let x = 0; x < width; x++) {
      const x0 = xs.i0[x];
      const x1 = xs.i1[x];
      const wx = xs.w[x];
      const top = m[row0 + x0] + (m[row0 + x1] - m[row0 + x0]) * wx;
      const bottom = m[row1 + x0] + (m[row1 + x1] - m[row1 + x0]) * wx;
      // Bildens egen alfa (t.ex. transparent PNG) räknas in i masken.
      const p = (y * width + x) * 4;
      const a = ((top + (bottom - top) * wy) / 255) * (data[p + 3] / 255);
      out[p] = data[p] * a + br * (1 - a);
      out[p + 1] = data[p + 1] * a + bg * (1 - a);
      out[p + 2] = data[p + 2] * a + bb * (1 - a);
      out[p + 3] = 255;
    }
  }
  return out;
}
